import { Button } from '@monksflow/monks-ui';
import { DownloadIcon, FileText } from 'lucide-react';
import type { AssetFile } from '../../assetDetail.types';
import { PdfViewer } from '../PdfViewer';
import { TextViewer } from '../TextViewer';
import { useTranslation } from 'react-i18next';

interface Props {
  file: AssetFile;
  /**
   * Embedded contexts (e.g. the Library preview modal) that don't host the
   * AssetDetail annotation store / fullscreen chrome. When true, the PDF
   * viewer drops the annotation overlay + Expand control that would otherwise
   * be dead here. Default: full AssetDetail chrome.
   */
  embedded?: boolean;
}

export function AssetPreview(props: Props) {
  const { downloadUrl, assetType, fileType, fileName } = props.file;

  if (!downloadUrl) {
    return <FileFallback />;
  }

  if (assetType === 'document' && fileType === 'application/pdf') {
    return (
      <PdfViewer
        file={props.file}
        annotationsEnabled={!props.embedded}
        showFullscreen={!props.embedded}
      />
    );
  }

  if (assetType === 'image') {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden rounded-tl-lg rounded-tr-lg bg-muted dark:bg-background">
        <img src={downloadUrl} alt={fileName} className="max-h-full max-w-full object-contain" />
      </div>
    );
  }

  if (assetType === 'video') {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden rounded-tl-lg rounded-tr-lg bg-black">
        <video src={downloadUrl} controls className="max-h-full max-w-full">
          <track kind="captions" />
        </video>
      </div>
    );
  }

  if (isInlineTextType(fileType, fileName)) {
    return <TextViewer downloadUrl={downloadUrl} fileName={fileName} />;
  }

  if (fileType.startsWith('audio/')) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-4 rounded-tl-lg rounded-tr-lg bg-muted dark:bg-background">
        <span className="text-sm text-muted-foreground">{fileName}</span>
        <audio src={downloadUrl} controls>
          <track kind="captions" />
        </audio>
      </div>
    );
  }

  return <FileFallback downloadUrl={downloadUrl} />;
}

/**
 * S3-served uploads often arrive as `application/octet-stream` instead of the
 * real MIME, so we also sniff a small allowlist of plain-text extensions when
 * the `fileType` header isn't `text/*` or `application/json`.
 */
// eslint-disable-next-line i18next/no-literal-string -- file extension allowlist
const INLINE_TEXT_EXTENSIONS = ['txt', 'log', 'md', 'csv', 'tsv', 'json', 'yaml', 'yml'];

function isInlineTextType(fileType: string, fileName: string): boolean {
  if (fileType.startsWith('text/') || fileType === 'application/json') {
    return true;
  }
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext !== undefined && INLINE_TEXT_EXTENSIONS.includes(ext);
}

/**
 * Shown when a file can't be previewed inline — either no signed URL, or a
 * type with no in-browser viewer (e.g. .docx). The modal header already shows
 * the filename, so this just explains the state and offers a download when a
 * URL is available.
 */
function FileFallback(props: { downloadUrl?: string | null }) {
  const { t } = useTranslation('projects');

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-3 rounded-tl-lg rounded-tr-lg bg-muted dark:bg-background">
      <FileText className="size-12 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">
        {t('projectDetail.assetPreview.previewNotAvailableForThis')}
      </span>
      {props.downloadUrl && (
        <Button
          variant="secondary"
          onClick={() => window.open(props.downloadUrl as string, '_blank', 'noopener,noreferrer')}
        >
          <DownloadIcon className="size-4" />
          {t('projectDetail.assetPreview.download')}
        </Button>
      )}
    </div>
  );
}
